import React from 'react';

type CombatBackdropFxProps = {
  lowFx: boolean;
  aggressiveLowFx?: boolean;
  combatTorchProgress: number;
  combatTorchCorruption: number;
  combatLightRadius: number;
  combatMadness: number;
  combatVignetteOpacity: number;
  combatVignettePulseOpacity: number;
  combatSanityScreenFlicker: any;
  combatSanityPeripheralShadow: any;
};

function CombatBackdropFx({
  lowFx,
  aggressiveLowFx = false,
  combatTorchProgress,
  combatTorchCorruption,
  combatLightRadius,
  combatMadness,
  combatVignetteOpacity,
  combatVignettePulseOpacity,
  combatSanityScreenFlicker,
  combatSanityPeripheralShadow,
}: CombatBackdropFxProps) {
  if (aggressiveLowFx) {
    return null;
  }

  const combatLightSpread = Math.max(0.35, Math.min(1.45, combatLightRadius / 4));
  const combatDarkR0 = 8 + combatLightSpread * 9;
  const combatDarkR1 = 18 + combatLightSpread * 15;
  const combatDarkR2 = 34 + combatLightSpread * 18;
  const combatDarkR3 = 54 + combatLightSpread * 18;
  const combatGlowR1 = 18 + combatLightSpread * 16;
  const combatGlowR2 = 38 + combatLightSpread * 18;
  const combatTorchLayerAnimation = combatTorchProgress > 0
    ? 'torchFlicker 1.15s steps(1, end) infinite'
    : undefined;
  const combatAmbientVignetteOpacity = Math.max(0.4, Math.min(1, 0.4 + (1 - combatTorchProgress) * 0.85));

  const combatExplorationDarknessStyle = React.useMemo(() => ({
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    animation: lowFx ? undefined : combatTorchLayerAnimation,
    backgroundImage: aggressiveLowFx
      ? `radial-gradient(circle at 50% 52%, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.26) ${(combatDarkR1 - 1).toFixed(2)}%, rgba(0,0,0,${combatTorchProgress > 0 ? 0.68 : 0.88}) ${(combatDarkR3 - 2).toFixed(2)}%)`
      : lowFx
        ? `radial-gradient(circle at 50% 52%, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.16) ${(combatDarkR1 + 2).toFixed(2)}%, rgba(0,0,0,${combatTorchProgress > 0 ? 0.58 : 0.82}) ${(combatDarkR3 + 4).toFixed(2)}%)`
      : [
          `radial-gradient(circle at 50% 52%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.10) ${combatDarkR0.toFixed(2)}%, rgba(0,0,0,0.28) ${combatDarkR1.toFixed(2)}%, rgba(0,0,0,0.62) ${combatDarkR2.toFixed(2)}%, rgba(0,0,0,${combatTorchProgress > 0 ? 0.72 : 0.95}) ${combatDarkR3.toFixed(2)}%)`,
          'radial-gradient(ellipse at 50% 46%, rgba(0,0,0,0) 24%, rgba(0,0,0,0.72) 100%)',
        ].join(','),
  }) as React.CSSProperties, [aggressiveLowFx, combatDarkR0, combatDarkR1, combatDarkR2, combatDarkR3, combatTorchLayerAnimation, combatTorchProgress, lowFx]);

  const combatExplorationGlowStyle = React.useMemo(() => ({
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    mixBlendMode: 'screen',
    filter: 'blur(2px)',
    animation: combatTorchLayerAnimation,
    backgroundImage: [
      `radial-gradient(circle at 50% 52%, rgba(255,200,130,${combatTorchProgress > 0 ? 0.22 * (1 - combatTorchCorruption * 0.75) : 0}) 0%, rgba(255,160,80,${0.10 * (1 - combatTorchCorruption * 0.85)}) ${combatGlowR1.toFixed(2)}%, rgba(0,0,0,0) ${combatGlowR2.toFixed(2)}%)`,
      `radial-gradient(circle at 50% 52%, rgba(170,110,255,${combatTorchProgress > 0 ? 0.04 + combatTorchCorruption * 0.18 : 0}) 0%, rgba(110,60,220,${combatTorchCorruption * 0.10}) ${(combatGlowR1 * 0.92).toFixed(2)}%, rgba(0,0,0,0) ${combatGlowR2.toFixed(2)}%)`,
    ].join(','),
  }) as React.CSSProperties, [combatGlowR1, combatGlowR2, combatTorchCorruption, combatTorchLayerAnimation, combatTorchProgress]);

  const combatAmbientVignetteStyle = React.useMemo(() => ({
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    background: aggressiveLowFx
      ? 'radial-gradient(ellipse at 50% 46%, rgba(0,0,0,0) 36%, rgba(0,0,0,0.58) 76%, rgba(0,0,0,0.84) 100%)'
      : lowFx
        ? 'radial-gradient(ellipse at 50% 46%, rgba(0,0,0,0) 28%, rgba(0,0,0,0.64) 70%, rgba(0,0,0,0.9) 100%)'
      : 'radial-gradient(ellipse at 50% 46%, rgba(0,0,0,0) 22%, rgba(0,0,0,0.72) 65%, rgba(0,0,0,0.94) 100%)',
    opacity: combatAmbientVignetteOpacity,
    transition: 'opacity 200ms ease-out',
  }) as React.CSSProperties, [aggressiveLowFx, combatAmbientVignetteOpacity, lowFx]);

  const sanityPrimaryStyle = React.useMemo(() => ({
    position: 'absolute',
    inset: 0,
    background: `radial-gradient(ellipse at center, rgba(0,0,0,0) 34%, rgba(72,24,120,${(combatVignetteOpacity * 0.35).toFixed(3)}) 58%, rgba(108,42,180,${(combatVignetteOpacity * 0.72).toFixed(3)}) 76%, rgba(32,0,58,${(combatVignetteOpacity * 1.1).toFixed(3)}) 100%)`,
    filter: lowFx ? 'none' : 'blur(1px)',
    transform: lowFx ? 'scale(1)' : `scale(${(1 + combatMadness * 0.025).toFixed(3)})`,
    opacity: aggressiveLowFx ? Math.max(0, combatVignetteOpacity * 0.82) : 1,
  }) as React.CSSProperties, [aggressiveLowFx, combatMadness, combatVignetteOpacity, lowFx]);

  const sanityPulseStyle = React.useMemo(() => ({
    position: 'absolute',
    inset: '-4%',
    background: `
      radial-gradient(circle at 18% 26%, rgba(160,80,255,${(combatVignettePulseOpacity * 0.65).toFixed(3)}) 0 10%, rgba(160,80,255,0) 34%),
      radial-gradient(circle at 82% 22%, rgba(120,50,220,${(combatVignettePulseOpacity * 0.52).toFixed(3)}) 0 10%, rgba(120,50,220,0) 36%),
      radial-gradient(circle at 14% 78%, rgba(170,90,255,${(combatVignettePulseOpacity * 0.56).toFixed(3)}) 0 10%, rgba(170,90,255,0) 34%),
      radial-gradient(circle at 84% 82%, rgba(110,40,210,${(combatVignettePulseOpacity * 0.48).toFixed(3)}) 0 10%, rgba(110,40,210,0) 38%)
    `,
    filter: 'blur(10px)',
    animation: 'combatSanityPulse 2.8s ease-in-out infinite',
    mixBlendMode: 'screen',
  }) as React.CSSProperties, [combatVignettePulseOpacity]);

  const sanityFlickerStyle = React.useMemo(() => {
    if (!combatSanityScreenFlicker || lowFx) return null;
    return {
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none',
      mixBlendMode: 'screen',
      filter: `blur(${combatSanityScreenFlicker.blurPx}px)`,
      background: `
        radial-gradient(ellipse at 50% 48%, rgba(180,110,255,${combatSanityScreenFlicker.violetAlpha}) 0%, rgba(120,60,220,${combatSanityScreenFlicker.violetAlpha * 0.55}) 34%, rgba(0,0,0,0) 72%),
        radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 26%, rgba(0,0,0,${combatSanityScreenFlicker.darknessAlpha}) 64%, rgba(0,0,0,${combatSanityScreenFlicker.darknessAlpha * 1.8}) 100%)
      `,
      animation: `combatSanityFlicker ${combatSanityScreenFlicker.durationMs}ms ease-out 1 both`,
      ['--combat-flicker-scale' as any]: combatSanityScreenFlicker.scale,
    } as React.CSSProperties;
  }, [combatSanityScreenFlicker, lowFx]);

  const sanityPeripheralShadowStyle = React.useMemo(() => {
    if (!combatSanityPeripheralShadow || lowFx) return null;
    const sideStyle = combatSanityPeripheralShadow.side === 'left'
      ? { left: 0, top: `${50 - combatSanityPeripheralShadow.heightPct / 2}%`, width: `${combatSanityPeripheralShadow.widthPct}%`, height: `${combatSanityPeripheralShadow.heightPct}%` }
      : combatSanityPeripheralShadow.side === 'right'
        ? { right: 0, top: `${50 - combatSanityPeripheralShadow.heightPct / 2}%`, width: `${combatSanityPeripheralShadow.widthPct}%`, height: `${combatSanityPeripheralShadow.heightPct}%` }
        : combatSanityPeripheralShadow.side === 'top'
          ? { top: 0, left: `${50 - combatSanityPeripheralShadow.widthPct / 2}%`, width: `${combatSanityPeripheralShadow.widthPct}%`, height: `${combatSanityPeripheralShadow.heightPct}%` }
          : { bottom: 0, left: `${50 - combatSanityPeripheralShadow.widthPct / 2}%`, width: `${combatSanityPeripheralShadow.widthPct}%`, height: `${combatSanityPeripheralShadow.heightPct}%` };

    return {
      position: 'absolute',
      zIndex: 1,
      pointerEvents: 'none',
      ...sideStyle,
      filter: `blur(${combatSanityPeripheralShadow.blurPx}px)`,
      background: 'radial-gradient(ellipse at center, rgba(20,0,30,0) 0%, rgba(25,0,45,0.16) 26%, rgba(0,0,0,0.34) 58%, rgba(0,0,0,0) 100%)',
      animation: `combatSanityShadowDrift ${combatSanityPeripheralShadow.durationMs}ms ease-out 1 both`,
      ['--combat-shadow-drift-x' as any]: combatSanityPeripheralShadow.side === 'left'
        ? `-${combatSanityPeripheralShadow.driftPx}px`
        : combatSanityPeripheralShadow.side === 'right'
          ? `${combatSanityPeripheralShadow.driftPx}px`
          : '0px',
      ['--combat-shadow-drift-y' as any]: combatSanityPeripheralShadow.side === 'top'
        ? `-${combatSanityPeripheralShadow.driftPx}px`
        : combatSanityPeripheralShadow.side === 'bottom'
          ? `${combatSanityPeripheralShadow.driftPx}px`
          : '0px',
      ['--combat-shadow-opacity' as any]: String(combatSanityPeripheralShadow.opacity),
    } as React.CSSProperties;
  }, [combatSanityPeripheralShadow, lowFx]);

  return (
    <>
      <div aria-hidden="true" style={combatExplorationDarknessStyle} />
      {!lowFx && !aggressiveLowFx && <div aria-hidden="true" style={combatExplorationGlowStyle} />}
      <div aria-hidden="true" style={combatAmbientVignetteStyle} />

      {combatVignetteOpacity > 0 && (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
          <div style={sanityPrimaryStyle} />
          {!lowFx && !aggressiveLowFx && <div style={sanityPulseStyle} />}
        </div>
      )}

      {!lowFx && !aggressiveLowFx && combatSanityScreenFlicker && sanityFlickerStyle && (
        <div
          key={`combat-sanity-flicker-${combatSanityScreenFlicker.id}`}
          aria-hidden="true"
          style={sanityFlickerStyle}
        />
      )}

      {!lowFx && !aggressiveLowFx && combatSanityPeripheralShadow && sanityPeripheralShadowStyle && (
        <div
          key={`combat-sanity-shadow-${combatSanityPeripheralShadow.id}`}
          aria-hidden="true"
          style={sanityPeripheralShadowStyle}
        />
      )}
    </>
  );
}

function areCombatBackdropFxPropsEqual(prev: CombatBackdropFxProps, next: CombatBackdropFxProps) {
  return (
    prev.lowFx === next.lowFx &&
    prev.aggressiveLowFx === next.aggressiveLowFx &&
    prev.combatTorchProgress === next.combatTorchProgress &&
    prev.combatTorchCorruption === next.combatTorchCorruption &&
    prev.combatLightRadius === next.combatLightRadius &&
    prev.combatMadness === next.combatMadness &&
    prev.combatVignetteOpacity === next.combatVignetteOpacity &&
    prev.combatVignettePulseOpacity === next.combatVignettePulseOpacity &&
    prev.combatSanityScreenFlicker === next.combatSanityScreenFlicker &&
    prev.combatSanityPeripheralShadow === next.combatSanityPeripheralShadow
  );
}

export default React.memo(CombatBackdropFx, areCombatBackdropFxPropsEqual);
